import { fas } from '@awesome.me/kit-b4fd369ea4/icons';
import { TitleButtonDivider } from '@/common/buttons/TitleButtonDivider';
import { EntityPerspective } from '@/common/entityFramework/entityPerspective/EntityPerspective';
import { useFilterButtons } from '@/common/entityFramework/entityPerspective/hooks/useFilterButtons';
import { RowIcon } from '@/common/entityFramework/entityPerspective/support/RowIcon';
import { ExprUtils } from '@/common/utils/ExprUtils';
import { useCallback, useRef } from 'react';

export const DemReservePerspective = () => {

  const entityPerspectiveRef = useRef();

  const views = [
    {
      name: 'Balances',
      dims: ['PAID_PERIOD', 'CHANNEL', 'PROD', 'EARNED_PERIOD'],
      kpis: ['START_BAL', 'ACCR_AMT', 'ACCR_ADJ', 'TOTAL_ACCR', 'PYMT_AMT', 'PYMT_ADJ', 'TOTAL_PYMT', 'END_BAL'],
      numFrozenDims: 1,
      dimToPeriodicities: {
        PAID_PERIOD: ['YEAR', 'QTR', 'MON'],
        EARNED_PERIOD: ['YEAR', 'QTR']
      }
    },

    {
      name: 'Aging',
      dims: ['EARNED_PERIOD', 'PAID_PERIOD', 'CHANNEL', 'PROD'],
      kpis: ['START_BAL', 'ACCR_AMT', 'ACCR_ADJ', 'TOTAL_ACCR', 'PYMT_AMT', 'PYMT_ADJ', 'TOTAL_PYMT', 'END_BAL'],
      numFrozenDims: 1,
      dimToPeriodicities: {
        PAID_PERIOD: ['YEAR', 'QTR', 'MON'],
        EARNED_PERIOD: ['YEAR', 'QTR']
      }
    }
  ]

  const { filterButtonStages, filterButtons, onViewReset } = useFilterButtons([
    {
      key: 'negEndBal',
      icon: fas.faExclamationTriangle,
      colors: [null, 'red', 'gree'],
      toolTips: ['Positive and negative end balance', 'Negative end balance', 'Positive end balance']
    }
  ])

  const augButtons = (buttons) => {
    buttons.push(...filterButtons, <TitleButtonDivider key="filterButtonDivider" />);
  }

  const augReqParam = useCallback((reqParam) => {

    // Negative end balance
    const negEndBalButtonState = filterButtonStages.negEndBal
    if (negEndBalButtonState > 0) {
      reqParam.expr = ExprUtils.and(
        reqParam.expr,
        negEndBalButtonState === 1 ? ExprUtils.whereParams('negBal', 1) : ExprUtils.isNot('negBal', 1)
      )
    }

  }, [filterButtonStages])

  const rowIconsFn = (data) => {

    let rowIcons = [];

    if (data.closed) {
      rowIcons.push(<RowIcon key="closed" icon={fas.faCheckCircle} color="green" toolTip="Period is closed" />);
    }

    if (data.negBal === 1) {
      rowIcons.push(<RowIcon key="negEndBal" icon={fas.faExclamationTriangle} color="red" toolTip="End balance is negative" />);
    } else if (data.negBal > 0) {
      rowIcons.push(<RowIcon key="containsNegEndBal" icon={fas.faExclamationTriangle} color="#FFC4C8" toolTip="Contains an end balance that is negative" />);
    }

    return rowIcons;
  }

  const getContextMenuItems = (params) => {
    let contextMenus = entityPerspectiveRef.current.getContextMenuItems(params);

    const navigationItems = [
      {
        name: 'Navigate to Payment Adjustments',
        icon: fas.faSlidersH,
        action: () => entityPerspectiveRef.current.navigateToPerspectiveAdj(params, 'PYMT_ADJ')
      },
      {
        name: 'Navigate to RCM Payments',
        icon: fas.faListUl,
        action: () => entityPerspectiveRef.current.navigateToViewer(params, 'PYMT_AMT', 'RCM Payments')
      },
    ];

    const adjustmentItems = [
      {
        name: 'Adjust Accrual Amount',
        icon: fas.faPencil,
        action: () => entityPerspectiveRef.current.overwrite(params, 'ACCR_ADJ')
      }
    ];

    contextMenus.push(
      {
        name: 'Transactions',
        icon: fas.faListUl,
        subMenu: navigationItems,
      },
      {
        name: 'Adjust',
        icon: fas.faPencil,
        subMenu: adjustmentItems,
      }
    );

    return contextMenus;
  }

  return (
    <EntityPerspective
      ref={entityPerspectiveRef}
      views={views}
      augButtons={augButtons}
      augReqParam={augReqParam}
      rowIconsFn={rowIconsFn}
      onViewReset={onViewReset}
      gridOptions={{
        getContextMenuItems
      }} />
  )

}